/**
 * M&M Confirmation Card
 *
 * Rendered inline inside the Ni² conversation the moment the intent
 * router matches an ADD_TO_MM utterance. Uses only workbook data for
 * the case metadata and writes to MMBuilderStore on confirm.
 *
 * The three states (draft, added, cancelled) are terminal — the card
 * never re-opens a confirmation for the same match because the store
 * marks it via `markConfirmation`.
 */
import { useMemo, useState } from "react";
import {
  BookText,
  Check,
  X,
  Pencil,
  ArrowUpRight,
  User,
  Stethoscope,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getCaseById, getPerson } from "@/data/repository";
import { useMMBuilderStore } from "@/context/MMBuilderStore";
import { cn } from "@/lib/utils";

const CLASSIFICATIONS = {
  outcomeCategory: ["Morbidity", "Mortality", "Near Miss", "Teaching Case"],
  severity: ["Minor", "Major", "Not Applicable"],
  preventability: ["Preventable", "Potentially Preventable", "Not Preventable", "Unclear"],
};

export default function MMConfirmationCard({ confId, payload }) {
  const store = useMMBuilderStore();
  const navigate = useNavigate();

  const caseRec = useMemo(() => getCaseById(payload?.caseId), [payload?.caseId]);
  const priorStatus = store.confirmations[confId];

  const [editing, setEditing] = useState(false);
  const [outcome, setOutcome] = useState("Morbidity");
  const [severity, setSeverity] = useState("Major");
  const [preventability, setPreventability] = useState("Potentially Preventable");

  if (!caseRec) {
    return (
      <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4 text-[12px] text-[hsl(var(--nn-ink-muted))]">
        Ni² could not resolve a matching workbook case for this request.
      </div>
    );
  }

  const attending = getPerson(caseRec.primaryAttendingId);
  const seniorResident = getPerson(caseRec.primaryResidentId);

  const alreadyAdded = priorStatus === "added";
  const cancelled = priorStatus === "cancelled";

  const onAdd = () => {
    if (alreadyAdded || cancelled) return;
    store.addCaseToMMSession(payload.caseId, { tagSenior: payload.tagSenior });
    store.markConfirmation(confId, "added");
    // Attach a discussion record so the collaboration ledger reflects the confirmation
    store.appendMMCollaborationRecord({
      authorPersonId: "ATT-001",
      comment: `Case ${payload.caseId} added to current M&M · ${outcome} · ${severity} · ${preventability}`,
      kind: "confirmation",
    });
  };

  const onCancel = () => {
    if (alreadyAdded) return;
    store.markConfirmation(confId, "cancelled");
  };

  const openBuilder = () => navigate("/mm");

  return (
    <div
      className="w-full max-w-[560px] nn-soft-in rounded-2xl border border-white/[0.10] bg-white/[0.04] p-4"
      data-testid={`mm-confirmation-card-${confId}`}
      style={{
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 12px 32px -16px rgba(0,0,0,0.55)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="grid h-7 w-7 place-items-center rounded-lg border border-white/[0.10] bg-white/[0.06] text-[hsl(var(--nn-amber))]">
            <BookText className="h-3.5 w-3.5" strokeWidth={1.8} />
          </span>
          <div>
            <div className="nn-eyebrow">Ni² · Structured intent</div>
            <div className="text-[13px] font-semibold tracking-tight text-white">
              Add case to M&M
            </div>
          </div>
        </div>
        <span
          className={cn(
            "nn-chip",
            alreadyAdded ? "nn-chip-ok" : cancelled ? "" : "nn-chip-amber"
          )}
          style={{ fontSize: 10 }}
        >
          {alreadyAdded ? "Added to queue" : cancelled ? "Cancelled" : "Awaiting confirmation"}
        </span>
      </div>

      {/* Case block */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <MetaField label="Case ID" value={caseRec.caseId} mono />
        <MetaField label="Patient" value={`${caseRec.patientLabel} · ${caseRec.age}${caseRec.sex === "Female" ? "F" : "M"}`} />
        <MetaField label="Diagnosis" value={`${caseRec.laterality || ""} ${caseRec.diagnosis}`.trim()} span={2} />
        <MetaField
          label="Complication"
          value={caseRec.otherComplication && caseRec.otherComplication !== "None" ? caseRec.otherComplication : "Postoperative facial weakness (HB IV)"}
          span={2}
        />
      </div>

      {/* Classification */}
      <div className="mt-4">
        <div className="nn-eyebrow flex items-center gap-2">
          <Sparkles className="h-2.5 w-2.5" strokeWidth={1.8} />
          Proposed M&M classification
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <ClassSelect label="Outcome" value={outcome} options={CLASSIFICATIONS.outcomeCategory} onChange={setOutcome} disabled={!editing || alreadyAdded || cancelled} />
          <ClassSelect label="Severity" value={severity} options={CLASSIFICATIONS.severity} onChange={setSeverity} disabled={!editing || alreadyAdded || cancelled} />
          <ClassSelect label="Preventability" value={preventability} options={CLASSIFICATIONS.preventability} onChange={setPreventability} disabled={!editing || alreadyAdded || cancelled} />
        </div>
      </div>

      {/* People */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <PersonPill Icon={Stethoscope} label="Primary attending" name={attending?.fullName} personId={attending?.personId} />
        <PersonPill
          Icon={User}
          label={payload.tagSenior ? "Assigned (tagged) senior resident" : "Assigned senior resident"}
          name={seniorResident?.fullName}
          personId={seniorResident?.personId}
          highlight={payload.tagSenior}
        />
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!alreadyAdded && !cancelled && (
          <>
            <button
              type="button"
              onClick={onAdd}
              className="nn-btn nn-btn-primary"
              data-testid={`mm-confirmation-add-${confId}`}
            >
              <Check className="h-3.5 w-3.5" strokeWidth={1.8} />
              Add to M&M
            </button>
            <button
              type="button"
              onClick={() => setEditing((e) => !e)}
              className="nn-btn"
              data-testid={`mm-confirmation-edit-${confId}`}
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.8} />
              {editing ? "Done editing" : "Edit"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="nn-btn"
              data-testid={`mm-confirmation-cancel-${confId}`}
            >
              <X className="h-3.5 w-3.5" strokeWidth={1.8} />
              Cancel
            </button>
          </>
        )}
        {alreadyAdded && (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--nn-ok))]/40 bg-[hsl(var(--nn-ok))]/10 px-3 py-1.5 text-[11.5px] font-medium text-[hsl(var(--nn-ok))]">
              <Check className="h-3 w-3" strokeWidth={2} />
              Case added to Current M&M
            </span>
            <button
              type="button"
              onClick={openBuilder}
              className="nn-btn nn-btn-primary"
              data-testid={`mm-confirmation-open-builder-${confId}`}
            >
              Open M&M Builder
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.8} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function MetaField({ label, value, mono, span = 1 }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5" style={{ gridColumn: `span ${span}` }}>
      <div className="text-[9.5px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
        {label}
      </div>
      <div className={cn("mt-1 text-[12px] text-[hsl(var(--nn-ink))]", mono && "font-mono")}>
        {value || "—"}
      </div>
    </div>
  );
}

function ClassSelect({ label, value, options, onChange, disabled }) {
  return (
    <label className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5">
      <div className="text-[9.5px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
        {label}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="mt-0.5 w-full bg-transparent text-[11.5px] text-[hsl(var(--nn-ink))] outline-none disabled:opacity-90"
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-[#101827]">{o}</option>
        ))}
      </select>
    </label>
  );
}

function PersonPill({ Icon, label, name, personId, highlight }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-xl border px-3 py-2",
        highlight
          ? "border-[hsl(var(--nn-blue))]/40 bg-[rgba(160,200,255,0.10)]"
          : "border-white/[0.06] bg-white/[0.02]"
      )}
    >
      <span className="grid h-8 w-8 place-items-center rounded-full border border-white/[0.10] bg-white/[0.04]">
        <Icon className="h-3.5 w-3.5 text-[hsl(var(--nn-ink-muted))]" strokeWidth={1.6} />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
          {label}
        </div>
        <div className="truncate text-[12px] text-[hsl(var(--nn-ink))]">
          {name || "—"}
        </div>
        <div className="font-mono text-[9.5px] text-[hsl(var(--nn-ink-faint))]">
          {personId || ""}
        </div>
      </div>
    </div>
  );
}
